export enum MeshType {
	Solid = 'solid',
	Mesh = 'mesh'
}

export const MESH_BAR_THICKNESS = 0.01 as const;
export const FINE_MESH_STEP_MIN = 0.03 as const;
export const FINE_MESH_STEP_MAX = 0.04 as const;

/** Thin grid lines in box direct view (matches Box.py INNER_LINE_WIDTH). */
export const BOX_INNER_MESH_LINE_WIDTH = 0.4 as const;

/** Port of Box.py _get_inner_lines_sections_qty (real units; scale-invariant). */
export function getBoxInnerMeshSectionsQty(availableLength: number): number {
	if (availableLength <= 0) {
		return 1;
	}

	const midStep = (FINE_MESH_STEP_MIN + FINE_MESH_STEP_MAX) / 2;
	let sections = Math.round(availableLength / midStep);
	const minSections = Math.ceil(availableLength / FINE_MESH_STEP_MAX);
	const maxSections = Math.floor(availableLength / FINE_MESH_STEP_MIN);

	if (minSections <= maxSections) {
		sections = Math.min(Math.max(sections, minSections), maxSections);
	}

	return Math.max(sections, 1);
}

/** Vertical inner line x offsets in real units (group origin top-left). */
export function getBoxInnerMeshVerticalOffsets(boxWidth: number, wallWidth: number): Array<number> {
	const verticalSpace = boxWidth - wallWidth;

	if (verticalSpace <= 0) {
		return [];
	}

	const sections = getBoxInnerMeshSectionsQty(verticalSpace);
	const step = verticalSpace / sections;
	const lines: Array<number> = [];

	for (let lineNumber = 1; lineNumber < sections; lineNumber++) {
		lines.push(wallWidth / 2 + step * lineNumber);
	}

	return lines;
}

/** Horizontal inner line y offsets in real units (from bottom edge, y-up). */
export function getBoxInnerMeshHorizontalOffsets(boxHeight: number): Array<number> {
	if (boxHeight <= 0) {
		return [];
	}

	const sections = getBoxInnerMeshSectionsQty(boxHeight);
	const step = boxHeight / sections;
	const lines: Array<number> = [];

	for (let lineNumber = 1; lineNumber <= sections; lineNumber++) {
		lines.push(step * lineNumber);
	}

	return lines;
}

/** Sliced box: bbox grid horizontals plus front/back partition step. */
export function getSlicedBoxInnerMeshHorizontalOffsets(frontHeight: number, backHeight: number): Array<number> {
	const maxHeight = Math.max(frontHeight, backHeight);
	const offsets = new Set(getBoxInnerMeshHorizontalOffsets(maxHeight));

	return [...offsets].sort((a, b) => a - b);
}

export function getMeshBarPlacements(
	length: number,
	minStep: number = FINE_MESH_STEP_MIN,
	maxStep: number = FINE_MESH_STEP_MAX
): Array<number> {
	if (length <= 0) {
		return [0];
	}

	const effectiveLength = length - MESH_BAR_THICKNESS;
	const effectiveMinStep = minStep + MESH_BAR_THICKNESS;
	const effectiveMaxStep = maxStep + MESH_BAR_THICKNESS;

	const minGaps = Math.ceil(effectiveLength / effectiveMaxStep);
	const averageGaps = Math.floor(effectiveLength / ((effectiveMinStep + effectiveMaxStep) / 2));
	const maxGaps = Math.floor(effectiveLength / effectiveMinStep);
	const gaps = minGaps <= maxGaps ? minGaps : Math.max(1, averageGaps);
	const step = effectiveLength / gaps;

	return Array.from({ length: gaps + 1 }, (_, index) => index * step);
}

export function getMeshBarCenters(length: number, bar: number = MESH_BAR_THICKNESS): Array<number> {
	const halfBar = bar / 2;

	return getMeshBarPlacements(length).map((offset) =>
		Math.min(Math.max(offset, halfBar), Math.max(length - halfBar, halfBar))
	);
}

export function getSlicedSlopeSpan(
	yBack: number,
	backHeight: number,
	frontHeight: number,
	depth: number
): { min: number; max: number } | null {
	if (depth <= 0 || yBack > backHeight + 1e-8) {
		return null;
	}

	const dy = frontHeight - backHeight;

	if (Math.abs(dy) < 1e-8) {
		return yBack < 0 ? null : { min: 0, max: depth };
	}

	let zMin = 0;
	let zMax = depth;
	const zFloor = (-yBack * depth) / dy;

	if (dy > 0) {
		if (yBack < 0) {
			zMin = Math.max(zMin, zFloor);
		}
	} else if (yBack + dy < 0) {
		zMax = Math.min(zMax, zFloor);
	}

	if (zMin >= zMax - 1e-8) {
		return null;
	}

	return { min: zMin, max: zMax };
}

export function getSlicedSlopeBars(
	backHeight: number,
	frontHeight: number,
	depth: number,
	bar: number = MESH_BAR_THICKNESS
): Array<{ yBack: number; min: number; max: number }> {
	const maxHeight = Math.max(backHeight, frontHeight);
	const yBackMin = Math.min(0, backHeight - frontHeight);

	return getSlicedBoxInnerMeshHorizontalOffsets(frontHeight, backHeight).flatMap((offset) => {
		const yBack = yBackMin + offset;
		const span = getSlicedSlopeSpan(yBack, backHeight, frontHeight, depth);

		return span ? [{ yBack, min: span.min, max: span.max }] : [];
	});
}
